import React, { useContext, useEffect, useRef, useState, forwardRef, useImperativeHandle, MutableRefObject } from 'react';
import { BryntumGantt, BryntumGanttProps } from '@bryntum/gantt-react';
import { ColumnStore, DateHelper, Gantt, PresetManager, ProjectModel, Store } from '@bryntum/gantt';
import { cloneDeep, isNil, round, uniq } from 'lodash';
import AlertTaskContext from '../../contexts/AlertTaskContext';
import { getRandomString } from '../../utils/commonUtil';
import '@bryntum/gantt/gantt.stockholm.css';
import { Button, Card, Col, Row, Space, Table, Radio, RadioChangeEvent, InputNumber } from 'antd';
import { getDayString } from '../../utils/DateUtil';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { MeteorEchart } from '@meteor/frontend-core';
import {
  FilterData,
  filterDataByDate,
  FilterType,
  syncEChartsToGantt,
  syncTimeRangeToECharts,
  getMaxAllowedRange,
  updateTasksCheckStatus,
} from './date-range-mapper';
import { EChartsOption } from 'echarts-for-react';
import { AppContext } from '../../contexts/AppContext';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import { calculateAreaTotal, calculateDisciplineTotal, calculateSubDisciplineTotal } from '../date-filtered-bar-chart/calculateAccFunc';
import jsPDF from 'jspdf';

// @flow
/**
 * @public
 */
export type DateFilterPosition = 'left' | 'center' | 'right';

export type BarChartData = {
  seriesName: string;
  date: Date;
  value: number;
  [key: string]: any;
};

export type FilterOptions = {
  label: string;
  value: FilterType;
};

export type DateFilterOption = {
  defaultFilterType?: FilterType;
  filterOptions?: FilterOptions[];
  position?: DateFilterPosition;
};

const defaultFilterOptions: FilterOptions[] = [
  { label: 'Year', value: 'Year' },
  { label: 'Month', value: 'Month' },
  { label: 'Week', value: 'Week' },
  { label: 'Day', value: 'Day' },
];

const defaultDateFilterOption: any = {
  defaultFilterType: 'Month',
  filterOptions: defaultFilterOptions,
  position: 'right',
};

const leftWidth = 440;

PresetManager.registerPreset('monthAndYear', {
  base: 'monthAndYear',
  tickWidth: 70, // 设置宽度
  headers: [
    {
      unit: 'YEAR',
      align: 'center',
      dateFormat: 'Y',
    },
    {
      unit: 'MONTH',
      align: 'center',
      dateFormat: 'MMM',
    },
  ],
});

const ganttConfig: BryntumGanttProps = {
  features: {
    regionResize: false,
    pdfExport: {
      exportServer: 'http://localhost:5100',
      exportDialog: {
        items: {
          fileFormatField: {
            value: 'pdf',
          },
        },
      },
    },
  },
  selectionMode: {
    multiSelect: false,
    dragSelect: false,
  },
  // appendTo: 'main',
  // flex: '1 1 auto',
  scrollTaskIntoViewOnCellClick: true,
  subGridConfigs: {
    fixed: {
      // No resizing of the fixed column region
      resizable: false,
      // Set a lower weight than the built-in "locked" section to have it appear to the left
      weight: 1,
    },
    locked: {
      // A scrollable section with the main columns
      width: leftWidth,
      weight: 2,
    },
  },
  columns: [
    {
      type: 'check',
      field: 'check',
      text: 'Pick',
      width: 60,
      filterable: {
        filterField: {
          type: 'combo',
          multiSelect: true,
          editable: false,
          items: [
            { value: true, text: 'Checked' },
            { value: false, text: 'Unchecked' },
          ],
        },
      },
    },
    {
      type: 'name',
      field: 'name',
      width: 240,
      editor: false,
      filterable: ({ value, record }) => {
        const names = value.split(' ').filter((n) => !!n);
        return !!record.name && names.some((name) => record.name.toUpperCase().includes(name.trim().toUpperCase()));
      },
    },
    {
      text: 'Activity ID',
      field: 'taskId',
      width: 160,
      renderer: (task) => `${task.record.taskId || '-'}`,
      editor: false,
      filterable: ({ value, record }) => {
        const ids = value.split(' ').filter((n) => !!n);
        return !!record.taskId && ids.some((id) => record.taskId.toUpperCase().includes(id.trim().toUpperCase()));
      },
    },
    {
      text: 'wbs',
      field: 'wbs',
      renderer: (task) => `${task.record.wbs || '-'}`,
      editor: false,
      filterable: ({ value, record }) => {
        const wbs = value.split(' ').filter((n) => !!n);
        return !!record.wbs && wbs.some((w) => record.wbs.toUpperCase().includes(w.trim().toUpperCase()));
      },
    },
    {
      text: 'discipline',
      field: 'discipline',
      sortable: false,
      editor: false,
      filterable: {
        filterField: {
          type: 'combo',
          multiSelect: true,
          editable: false,
        },
      },
    },
    {
      text: 'CWA',
      field: 'area',
      renderer: (task) => `${task.record.area || '-'}`,
      sortable: false,
      filterable: {
        filterField: {
          type: 'combo',
          multiSelect: true, // 改为多选
          editable: false,
        },
      },
      editor: false,
    },
    {
      text: 'package',
      field: 'package',
      renderer: (task) => `${task.record.package || '-'}`,
      sortable: false,
      filterable: {
        filterField: {
          type: 'combo',
          multiSelect: true, // 改为多选
          editable: false,
        },
      },
      editor: false,
    },
    { type: 'startdate', editor: false },
    { type: 'enddate', editor: false },
    {
      text: 'duration',
      field: 'durOrg',
      editor: false,
      filterable: ({ value, record }) => {
        const durs = value
          .split(' ')
          .filter((n) => !!n)
          .map((d) => Number(d.trim()))
          .filter((d) => !Number.isNaN(d));
        return !!record.durOrg && durs.some((dur) => record.durOrg.toString().includes(dur.toString()));
      },
    },
    {
      type: 'predecessor',
      editor: false,
      filterable: ({ value, record }) => {
        const pds = value.split(' ').filter((n) => !!n);
        const predecessorsIds = record.predecessors.map((p) => p.from).join(';');
        return !!predecessorsIds && pds.some((pd) => predecessorsIds.toUpperCase().includes(pd.trim().toUpperCase()));
      },
    },
  ],
  barMargin: 5,
  rowHeight: 20,
  cls: 'gantt-bar',
  project: {
    // startDate: '2023-03-01',
    // endDate: '2024-12-09',
    taskStore: {
      fields: [
        'check',
        'name',
        'taskId',
        'wbs',
        'discipline',
        'area',
        'package',
        'startdate',
        'enddate',
        'duration',
        'predecessor',
        'fwbsLevel',
        'cstrDate',
        'mpPerDay',
        'durOrg',
      ],
    },
    stm: {
      autoRecord: true,
    },
    resetUndoRedoQueuesAfterLoad: true,
  },
  viewPreset: 'monthAndYear',
  dependenciesFeature: {
    allowCreate: false,
    drawOnScroll: false,
  },
  percentBarFeature: {
    allowResize: false,
  },
  taskEditFeature: false,
  taskMenuFeature: false,
  taskDragFeature: false,
  taskResizeFeature: false,
  taskDragCreateFeature: false,
  criticalPathsFeature: {
    disabled: true,
  },
  filterFeature: true,
  progressLineFeature: {
    disabled: true,
  },
  taskNonWorkingTimeFeature: true,
  baselinesFeature: {
    disabled: false,
    template: (item): string => {
      const { baseline } = item;
      const { task } = baseline;

      const displayDuration = baseline.duration;

      return `<div class="b-gantt-task-title">
              ${task.name} (${baseline.name})</div>
              <table>
                <tr>
                  <td>Start:</td>
                  <td>${item.startClockHtml}</td>
                </tr>
                <tr>
                  <td>End:</td>
                  <td>${item.endClockHtml}</td>
                </tr>
                <tr>
                  <td>Duration:</td>
                  <td class="b-right">${`${displayDuration} ${DateHelper.getLocalizedNameOfUnit(
                    baseline.durationUnit,
                    baseline.duration !== 1,
                  )}`}</td>
                </tr>
              </table>
              `;
    },
  },
  timeRangesFeature: true,
  indicatorsFeature: {
    items: {
      earlyDates: false,
      lateDates: false,
      deadlineDate: false,
      constraintDate: false,
      cstrDateIndicator: (taskRecord) =>
        taskRecord.cstrDate && ['MIL-', 'ROS-', 'ERD-'].some((startKey) => taskRecord.taskId.startsWith(startKey))
          ? {
              startDate: new Date(taskRecord.cstrDate),
              name: 'Constraint Date',
              iconCls: 'b-cus-diamond',
            }
          : null,
    },
    tooltipTemplate: (data) => {
      const {
        indicator: {
          taskRecord: { name, startDate, endDate, duration, percentDone },
        },
        startText,
      } = data;
      return `<div>
        <div style="font-weight: bolder">${name}</div>
        <div style="display: flex; justify-content: space-between;">
          <div>
            ${!isNil(startDate) ? `<div>Start: </div>` : ''} 
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>End: </div>` : ''}
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>Duration: </div>` : ''}
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>Complete: </div>` : ''}
            <div>Constraint: </div>
          </div>
          <div style="margin-left: 5px; text-align: right">
            ${!isNil(startDate) ? `<div>${DateHelper.format(startDate, 'MMM DD, YYYY')}</div>` : ''} 
            ${
              !isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>${DateHelper.format(endDate, 'MMM DD, YYYY')}</div>` : ''
            } 
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>${round(duration, 1)} days</div>` : ''}
            ${!isNil(endDate) && startDate.getTime() !== endDate.getTime() ? `<div>${percentDone * 100}%</div>` : ''}
            <div>${startText}</div>
          </div>
        </div>
      </div>`;
    },
  },
};

const originalFetch = window.fetch;
const grabHtml = (htmlContainer: MutableRefObject<{ html: any }>, httpAddress: string) => {
  window.fetch = async function (...args) {
    const [url, options] = args;
    if (url === httpAddress) {
      const html = JSON.parse(options.body as string).html.map((h) => h.html);
      htmlContainer.current.html = html;
      return null;
    }
    window.fetch = originalFetch;
    return originalFetch(...args);
  };
};

const GanttChartWithMp: React.FC<any> = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const {
    data,
    modeOperate,
    optimizationId,
    calenderOrg,
    accumulation,
    peakData,
    chartOption,
    widget,
    exportImg,
    exportPDF,
    saveActiveTask,
    setData,
    area,
    resourceLoadingConstraint,
    resourceType,
    optimizationData,
    openSaveFilterModal,
    openLoadFilterModal,
  } = props;

  const [mode, modeDispatch] = modeOperate;
  const gantt = useRef<any>(null);
  const [ganttKey, setGanttKey] = useState<string>();
  const { alertView, setAlertView } = useContext(AlertTaskContext);

  const ganttInstance = (): Gantt => gantt.current?.instance;
  const ganttProject = (): ProjectModel => ganttInstance().project;
  const ganttStore = (): Store => ganttInstance().store as Store;
  const ganttColumns = () => ganttInstance().columns as ColumnStore;
  const lastRangeRef = useRef({ start: null, end: null });
  const projectReadyRef = useRef(false);
  const [projectReady, setProjectReady] = useState(false);
  const [scrollSourceRef, setScrollSourceRef] = useState('user');
  const scrollRef = useRef(false);
  const [calenderData, setCalenderData] = useState<any[]>();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [height, setHeight] = useState<number>(props.height);
  const { color, echartTheme } = useContext(AppContext);
  const accumulationRef = useRef(null);
  const timelineRef = useRef([]);
  const MaxAllowedRangeRef = useRef(null);
  const ganttFilterRef = useRef(null);

  const defaultOption: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      appendToBody: true,
      className: 'overflow-all',
      hideDelay: 0,
      order: 'seriesDesc',
    },
    xAxis: {
      type: 'category',
      axisTick: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisLabel: {
        show: true,
      },
      z: 10,
    },
    yAxis: {
      name: 'MP',
      position: 'left',
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '20%',
      height: '70%',
      top: '10%',
      containLabel: true,
    },
    dataZoom: [
      {
        type: 'slider',
        filterMode: 'filter',
        zoomOnMouseWheel: false,
        moveOnMouseMove: false,
        zoomOnDoubleClick: false,
      },
    ],
    series: [],
  };
  const [filterOption, setFilterOption] = useState<DateFilterOption>(defaultDateFilterOption);
  const [filterType, setFilterType] = useState<FilterType>(filterOption.defaultFilterType || defaultDateFilterOption.defaultFilterType);
  const filterTypeRef = useRef(filterOption.defaultFilterType || defaultDateFilterOption.defaultFilterType);
  const [option, setOption] = useState<EChartsOption>({
    ...defaultOption,
    ...chartOption,
  });

  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const filterDataList: any = {};
  const instance = useRef(null);
  const minMaxRangeRef = useRef({ min: dayjs(), max: dayjs() });

  const setAlertColor = () => {
    return {
      ...option,
      series: option.series.map((item) => {
        if (item.name === alertView.seriesName) {
          return {
            ...item,
            data: item.data.map((value, index) => {
              if (index === alertView.index) {
                if (value.itemStyle) {
                  return {
                    ...value,
                    itemStyle: { borderColor: 'red', borderWidth: 2 },
                  };
                }
                return {
                  value: value,
                  itemStyle: { borderColor: 'red', borderWidth: 2 },
                };
              }
              return value;
            }),
          };
        }
        return item;
      }),
    };
  };

  useEffect(() => {
    if (!alertView || alertView.tab !== 'S&R' || !alertView.seriesName) {
      return;
    }
    if (instance && instance.current) {
      const curr: any = instance.current;
      const inst = curr.getEchartsInstance();
      const op = setAlertColor();
      inst.setOption(op);
      setOption({
        ...op,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertView]);

  const isFilterChange = useRef(false);

  useEffect(() => {
    if (!filterType || !option || !projectReadyRef.current) {
      return;
    }
    const filtered =
      filterDataList[filterType] ||
      filterDataByDate(
        filterType,
        peakData.filter((item) => item.seriesName).map((item) => ({ ...item, day: item.date.toDateString() })),
        ganttProject(),
        ganttInstance().visibleDateRange,
      );

    if (!filterDataList[filterType]) {
      // cache data
      filterDataList[filterType] = filtered;
    }

    filtered.series = filtered.series.sort((a: any, b: any) => {
      const order = { H: 2, M: 1, L: 0 };
      const contains = Object.keys(order);
      if (contains.includes(a.name) && contains.includes(b.name)) {
        return order[a.name] - order[b.name];
      }
      if (typeof a.name === 'string' && typeof b.name === 'string') {
        return b.name > a.name ? 1 : -1;
      }
      return b.name - a.name;
    });
    setFilterData(filtered);
    if (filterType === 'Month') {
      timelineRef.current = filtered.timeline;
    }
    isFilterChange.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  useEffect(() => {
    if (!peakData || !projectReadyRef.current) {
      return;
    }
    const filtered = filterDataByDate(
      filterType,
      peakData.filter((item) => item.seriesName).map((item) => ({ ...item, day: item.date.toDateString() })),
      ganttProject(),
      ganttInstance().visibleDateRange,
    );

    if (!filterDataList[filterType]) {
      // cache data
      filterDataList[filterType] = filtered;
    }
    filtered.series = filtered.series.sort((a: any, b: any) => {
      const order = { H: 2, M: 1, L: 0 };
      const contains = Object.keys(order);
      if (contains.includes(a.name) && contains.includes(b.name)) {
        return order[a.name] - order[b.name];
      }
      if (typeof a.name === 'string' && typeof b.name === 'string') {
        return b.name > a.name ? 1 : -1;
      }
      return b.name - a.name;
    });
    setFilterData(filtered);
    if (filterType === 'Month') {
      timelineRef.current = filtered.timeline;
    }
    isFilterChange.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peakData, projectReady]);

  useEffect(() => {
    if (!filterData || !projectReadyRef.current) {
      return;
    }
    const newOption = cloneDeep(option);
    newOption.xAxis.data = filterData.timeline;

    const baseSeries = {
      type: 'bar',
      stack: 'mp',
      barCategoryGap: filterType === 'Day' ? '0%' : '20%',
    };
    const totalSeries = {
      name: 'total',
      type: 'bar',
      itemStyle: {
        color: '#da7705',
        borderColor: '#da7705',
        opacity: 0,
      },
      barGap: '-100%',
      data: filterData.timeline.map((item, index) => {
        let value = 0;
        filterData.series.forEach((seriesData) => {
          value += seriesData.values[index];
        });
        return round(value);
      }),
    };
    newOption.series = [];
    newOption.series.push(totalSeries);
    filterData.series.forEach((item) => {
      const seriesInfo: any = {
        ...baseSeries,
        name: item.name,
        data: item.values.map((value) => round(value)),
      };
      accumulationRef.current = accumulation;
      if (accumulation === 'category' && item.name) {
        seriesInfo.itemStyle = {
          color: (() => {
            if (item.name === 'H') {
              return color.errorColor;
            }
            if (item.name === 'M') {
              return color.warningColor;
            }
            if (item.name === 'L') {
              return color.successColor;
            }
            return '';
          })(),
        };
      }
      newOption.series.push(seriesInfo);
    });

    newOption.legend.data = newOption.series
      .filter((item) => item.name !== 'total')
      .map((item) => ({
        ...item,
        // nameはタイプnumberの可能性がある、必ずタイプstringになります
        name: item.name ? item.name.toString() : '',
      }))
      .sort((a, b) => {
        const order = { H: 2, M: 1, L: 0 };
        const contains = Object.keys(order);
        if (contains.includes(a.name) && contains.includes(b.name)) {
          return order[b.name] - order[a.name];
        }
        if (typeof a.name === 'string' && typeof b.name === 'string') {
          return b.name < a.name ? 1 : -1;
        }
        return b.name - a.name;
      });

    let legendTotalWidth = 0;
    const legendItemWidth = 25;
    const letterWidth = 10;
    const offset = 100;

    newOption.legend.data.forEach((item) => {
      legendTotalWidth += legendItemWidth + item.name.length * letterWidth;
    });

    if (typeof document !== 'undefined') {
      const container = document.getElementsByClassName('ant-layout-content');

      if (container.length > 0) {
        if (legendTotalWidth > container[0].clientWidth - offset) {
          newOption.grid.top = 80;
        } else {
          newOption.grid.top = 60;
        }
      }
    }

    const acc = accumulationRef.current?.split('Name')?.[0];
    if (ganttFilterRef.current?.length && !!acc) {
      const f = ganttFilterRef.current.find((f) => f.id === acc);
      // const f = ganttFilterRef.current.find((f) => acc.toUpperCase().includes(f.id.toUpperCase()));
      if (f) {
        const selected = Object.fromEntries(
          newOption.legend.data.map((l) => [l.name, !f.value.length || l.name === 'total' || f.value.includes(l.name)]),
          // newOption.legend.data.map((l) => [
          //   l.name,
          //   !f.value.length || l.name === 'total' || acc === 'discipline'
          //     ? f.value.includes(l.name)
          //     : f.value.some((v) => l.name.startsWith(v)),
          // ]),
        );
        newOption.legend.selected = selected;
      }
    }

    if (alertView && alertView.tab === 'S&R' && alertView.seriesName) {
      newOption.series = setAlertColor().series;
    }
    if (MaxAllowedRangeRef.current && MaxAllowedRangeRef.current.notUsed) {
      newOption.dataZoom[0].start = MaxAllowedRangeRef.current.start;
      newOption.dataZoom[0].end = MaxAllowedRangeRef.current.end;
      MaxAllowedRangeRef.current.notUsed = false;
    }
    const curr: any = instance.current;
    const inst = curr?.getEchartsInstance();
    if (inst) {
      inst.clear();
      inst.setOption(newOption);
      setOption(newOption);
      const yAxisModel = inst.getModel().getComponent('yAxis', 0);
      if (yAxisModel) {
        const actualMax = yAxisModel.axis.scale._extent[1];
        setDefaultMaxValue(actualMax);
        setMpMaxValue(actualMax);
      }

      inst.dispatchAction({
        type: 'dataZoom',
        dataZoomIndex: 0,
        start: newOption.dataZoom[0].start,
        end: newOption.dataZoom[0].end,
      });
    }

    const initialSelectedState = {};
    initialSelectedState['selected'] = inst.getOption().legend[0].data.reduce((acc, seriesItem) => {
      const seriesName = seriesItem.name;
      acc[seriesName] = true;
      return acc;
    }, {});
    legendselectchange(inst, initialSelectedState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, projectReady]);

  const legendselectchange = (inst: any, params: any) => {
    if (optimizationData?.optimize?.optimizeName !== '設計' && optimizationData?.optimize?.optimizeName !== '工事') {
      return;
    }
    const theType = optimizationData?.optimize?.optimizeName === '設計' ? 1 : 2;
    let count = 0;
    switch (accumulationRef.current) {
      case 'areaName':
        if (!area || area.length <= 0) {
          return;
        }
        count = calculateAreaTotal(params.selected, theType, { area });
        break;
      case 'disciplineName':
        if (!resourceType || resourceType.length <= 0 || !resourceLoadingConstraint || resourceLoadingConstraint.length <= 0) {
          return;
        }
        count = calculateDisciplineTotal(params.selected, theType, { resourceType, resourceLoadingConstraint });
        break;
      case 'subDisciplineName':
        if (!resourceType || resourceType.length <= 0 || !resourceLoadingConstraint || resourceLoadingConstraint.length <= 0) {
          return;
        }
        count = calculateSubDisciplineTotal(params.selected, theType, { resourceType, resourceLoadingConstraint });
        break;
      default:
        break;
    }
    inst.setOption({
      series: [
        {
          markLine: {
            symbol: ['none', 'none'],
            silent: true,
            lineStyle: {
              color: '#ff4d4f',
              type: 'dashed',
              width: 3,
            },
            data: [
              {
                yAxis: count,
              },
            ],
          },
        },
      ],
    });
  };

  const onTimeTypeChange = ({ target: { value } }: RadioChangeEvent) => {
    setFilterType(value);
    filterTypeRef.current = value;
  };

  const onFeaturesClick = ({ source: item }) => {
    if (item.feature) {
      const feature = ganttInstance().features[item.feature];
      feature.disabled = !feature.disabled;
    } else if (item.subGrid) {
      const subGrid = ganttInstance().subGrids[item.subGrid];
      subGrid.collapsed = !subGrid.collapsed;
      setCollapsed(subGrid.collapsed);
      if (!subGrid.collapsed) {
        setHeight(props.height);
      } else {
        setHeight(props.height + 320);
      }
    } else if (item.toggleConfig) {
      ganttInstance[item.toggleConfig] = item.checked;
    }
  };

  const onFeaturesShow = ({ source: menu }) => {
    menu.items.map((item) => {
      const { feature } = item;

      if (feature) {
        // a feature might be not presented in the gantt
        // (the code is shared between "advanced" and "php" demos which use a bit different set of features)
        if (ganttInstance().features[feature]) {
          item.checked = !ganttInstance().features[feature].disabled;
        } else {
          // hide not existing features
          item.hide();
        }
      } else if (item.subGrid) {
        item.checked = ganttInstance().subGrids[item.subGrid].collapsed;
      }
    });
  };

  const onSettingsShow = ({ source: menu }) => {
    const { rowHeight, barMargin } = menu.widgetMap;

    rowHeight.value = ganttInstance().rowHeight;
    barMargin.value = ganttInstance().barMargin;
    barMargin.max = ganttInstance().rowHeight / 2 - 5;
  };

  const onRowHeightChange = ({ value, source }) => {
    ganttInstance().rowHeight = value;
    source.owner.widgetMap.barMargin.max = value / 2 - 5;
  };

  const onBarMarginChange = ({ value }) => {
    ganttInstance().barMargin = value;
  };

  const [ganttCardConfig, setGanttCardConfig] = useState(ganttConfig);

  const scrollToTask = (record) => {
    ganttInstance()
      .expandTo(record)
      .then(() => {
        try {
          ganttInstance()
            .scrollTaskIntoView(record, {
              animate: true,
              block: 'center',
              highlight: true,
              focus: true,
            })
            .then(() => {
              ganttInstance().selectRow(record);
            });
        } catch (error) {
          console.log(error);
        }
      });
  };

  useEffect(() => {
    if (!alertView || alertView.tab !== 'S&R' || !alertView.key) {
      return;
    }
    if (alertView.zoom) {
      ganttInstance().zoomTo({
        preset: 'monthAndYear',
        centerDate: new Date(alertView.startDate),
      });
    } else {
      const recordIndex = ganttStore().allRecords.findIndex((item: any) => item.taskId === alertView.key);
      if (recordIndex > -1) {
        scrollToTask(ganttStore().allRecords[recordIndex]);
      }
    }
  }, [alertView]);

  useEffect(() => {
    if (!data) {
      return;
    }
    setGanttKey(getRandomString());
  }, [data]);

  const exportHtmlRef = useRef({ html: null, config: null });
  const printPdf = () => {
    window.fetch = originalFetch;
    if (exportHtmlRef.current.html) {
      const { html, config } = exportHtmlRef.current;
      let copiedHtml = html[0];
      copiedHtml = copiedHtml.replace(/#b-gantt-4 \{ height: (\d+)px !important;.+?\}/, (match, p1) =>
        match.replace(p1, parseFloat(p1) + 27),
      );
      copiedHtml = copiedHtml.replace(/width: 11.69in; height: 16.49in;/g, 'overflow: auto');
      copiedHtml = copiedHtml.replace(
        /\.b-export-content \{ transform: scale\(.+?\) ; transform-origin: top left; height: auto !important; \}/,
        (match, p1) => '.b-export-content { height: auto !important; }',
      );
      const blob = new Blob([copiedHtml], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GANTT_${dayjs().format('YYYY-MM-DD hh:mm:ss')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    setGanttCardConfig({
      ...ganttConfig,
      tbar: {
        items: [
          {
            type: 'combo',
            text: 'FWBS Lv',
            editable: false,
            value: '',
            items: {
              '': t('aipskd.optionDetail.all'),
              'FWBS Level 1': 'FWBS Level 1',
              'FWBS Level 4': 'FWBS Level 4',
              'FWBS Level 5': 'FWBS Level 5',
            },
            onSelect: ({ source, record, records, userAction }) => {
              if (record.value) {
                ganttInstance().taskStore.filter({
                  filters: (task) => {
                    return task.fwbsLevel === record.value;
                  },
                  replace: true,
                });
              } else {
                ganttInstance().taskStore.clearFilters();
              }
            },
          },
          {
            type: 'buttonGroup',
            items: [
              {
                ref: 'expandAllButton',
                icon: 'b-fa b-fa-angle-double-down',
                tooltip: 'Expand all',
                onAction: () => {
                  ganttInstance().expandAll();
                },
              },
              {
                ref: 'collapseAllButton',
                icon: 'b-fa b-fa-angle-double-up',
                tooltip: 'Collapse all',
                onAction: () => {
                  ganttInstance().collapseAll();
                },
              },
            ],
          },
          {
            type: 'buttonGroup',
            items: [
              {
                ref: 'zoomInButton',
                icon: 'b-fa b-fa-search-plus',
                tooltip: 'Zoom in',
                onAction: () => {
                  ganttInstance().zoomIn();
                },
              },
              {
                ref: 'zoomOutButton',
                icon: 'b-fa b-fa-search-minus',
                tooltip: 'Zoom out',
                onAction: () => {
                  const { startDate, endDate } = ganttInstance().visibleDateRange as any;
                  const s = dayjs(startDate);
                  const e = dayjs(endDate);
                  const { min, max } = minMaxRangeRef.current;
                  if (s.isBefore(min) && e.isAfter(max)) return;
                  ganttInstance().zoomOut();
                },
              },
              {
                ref: 'zoomToFitButton',
                icon: 'b-fa b-fa-compress-arrows-alt',
                tooltip: 'Zoom to fit',
                onAction: () => {
                  ganttInstance().zoomTo({
                    preset: 'monthAndYear',
                  });
                },
              },
              {
                ref: 'previousButton',
                icon: 'b-fa b-fa-angle-left',
                tooltip: 'Previous time span',
                onAction: () => {
                  ganttInstance().shiftPrevious();
                },
              },
              {
                ref: 'nextButton',
                icon: 'b-fa b-fa-angle-right',
                tooltip: 'Next time span',
                onAction: () => {
                  ganttInstance().shiftNext();
                },
              },
            ],
          },
          {
            type: 'button',
            ref: 'featuresButton',
            icon: 'b-fa b-fa-tasks',
            text: 'Settings',
            tooltip: 'Toggle features',
            menu: {
              onItem: onFeaturesClick,
              onBeforeShow: onFeaturesShow,
              items: [
                {
                  text: 'UI settings',
                  icon: 'b-fa-sliders-h',
                  menu: {
                    cls: 'settings-menu',
                    layoutStyle: {
                      flexDirection: 'column',
                    },
                    onBeforeShow: onSettingsShow,
                    defaults: {
                      type: 'slider',
                      showValue: true,
                    },
                    items: [
                      {
                        ref: 'rowHeight',
                        text: 'Row height',
                        min: 20,
                        max: 70,
                        onInput: onRowHeightChange,
                      },
                      {
                        ref: 'barMargin',
                        text: 'Bar margin',
                        min: 0,
                        max: 10,
                        onInput: onBarMarginChange,
                      },
                    ],
                  },
                },
                {
                  text: 'Draw relations',
                  feature: 'dependencies',
                  checked: false,
                },
                {
                  text: 'Project lines',
                  feature: 'projectLines',
                  checked: false,
                },
                {
                  text: 'Show column lines',
                  feature: 'columnLines',
                  checked: true,
                },
                {
                  text: 'Hide schedule',
                  cls: 'b-separator',
                  subGrid: 'normal',
                  checked: false,
                },
              ],
            },
          },
          {
            type: 'button',
            text: 'Save',
            tooltip: 'Save Check Status',
            toggleable: true,
            onAction: (event) => {
              if (event.source.pressed) {
                saveActiveTask(
                  ganttStore()
                    .allRecords.filter((record: any) => record.check)
                    .map((record: any) => record.id),
                );
              }
            },
          },
          {
            type: 'button',
            text: 'Load Filter',
            onAction: () => {
              openLoadFilterModal();
            },
          },
          {
            type: 'button',
            text: 'Save Filter',
            onAction: () => {
              openSaveFilterModal();
            },
          },
          {
            type: 'button',
            text: 'テーブルモード',
            tooltip: 'table model',
            toggleable: true,
            onAction: (event) => {
              const normalSubGrid = (ganttInstance().subGrids as any).normal;
              normalSubGrid.collapsed = !normalSubGrid.collapsed;
              setCollapsed(normalSubGrid.collapsed);
              if (!normalSubGrid.collapsed) {
                setHeight(props.height);
                setTimeout(() => {
                  initScrollContainer();
                }, 2000);
              } else {
                setHeight(props.height + 320);
                scrollRef.current = false;
              }
            },
          },
          {
            type: 'button',
            text: 'Export',
            cls: 'related-path-btn',
            menu: {
              items: [
                {
                  type: 'button',
                  text: 'GANTT 出力',
                  tooltip: 'GANTT Output',
                  cls: 'settings-menu',
                  // disabled: true,
                  onAction: () => {
                    exportHtmlRef.current.html = null;
                    exportHtmlRef.current.config = null;
                    grabHtml(exportHtmlRef, (ganttInstance().features.pdfExport as any).exportServer);
                    ganttInstance()
                      .features.pdfExport.export({
                        fileFormat: 'pdf',
                        orientation: 'portrait',
                        paperFormat: 'A3',
                        exportType: 'singlepage',
                        showErrorToast: false,
                      })
                      .then((response) => {})
                      .catch((err) => {
                        document
                          .querySelector('.b-float-root.b-outer.b-windows.b-visible-scrollbar.b-chrome.b-theme-stockholm')
                          .replaceChildren();
                      })
                      .finally(() => {
                        printPdf();
                      });
                  },
                },
                {
                  type: 'button',
                  text: 'MP 出力',
                  tooltip: 'MP Output',
                  cls: 'settings-menu',
                  onAction: () => {
                    handleExportMPChart();
                  },
                },
              ],
            },
          },
        ],
      },
    });
  }, [optimizationId]);

  const handleExportMPChart = () => {
    const echarts = instance.current.getEchartsInstance();
    const { xAxis: cachedXAxis, series: cachedSeries, dataZoom: cachedDataZoom } = echarts.getOption();
    const { timeline, series } = filterDataByDate(
      filterTypeRef.current,
      peakData.filter((item) => item.seriesName).map((item) => ({ ...item, day: item.date.toDateString() })),
      ganttProject(),
      minMaxRangeRef.current,
    );
    echarts.setOption({
      xAxis: { data: timeline },
      series: series.map((s) => ({ name: s.name, data: s.values })),
      dataZoom: [
        {
          startValue: (minMaxRangeRef.current as any).startDate,
          endValue: (minMaxRangeRef.current as any).endDate,
        },
      ],
    });
    setTimeout(() => {
      const url = echarts.getDataURL({
        pixelRatio: 2,
        backgroundColor: '#fff',
      });
      const pdf = new jsPDF('l', 'mm', 'a2');
      const imgProps = pdf.getImageProperties(url);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(url, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`MP_${dayjs().format('YYYY-MM-DD hh:mm:ss')}.pdf`);
      echarts.setOption({
        xAxis: cachedXAxis,
        series: cachedSeries,
        dataZoom: cachedDataZoom,
      });
    }, 1000);
  };
  const peakDataRef = useRef(peakData);
  useEffect(() => {
    peakDataRef.current = peakData;
  }, peakData);

  const initScrollContainer = () => {
    // 横軸を上下の画面で連動させる
    const timelineScrollContainer = ganttInstance().element.querySelector(
      '[data-reference="virtualScrollerElement"][data-region="normal"]',
    );

    // ガント -> リソース山積み
    const handleHorizontalScrollA = () => {
      if (scrollSourceRef === 'dataZoom') {
        setScrollSourceRef('user');
        return;
      }
      const timeline = syncTimeRangeToECharts(
        filterTypeRef.current,
        ganttInstance(),
        instance.current?.getEchartsInstance(),
        timelineRef.current,
        peakDataRef.current.filter((item) => item.seriesName).map((item) => ({ ...item, day: item.date.toDateString() })),
      );
      timelineRef.current = timeline;
    };

    // リソース山積み -> ガント
    const handleHorizontalScrollB = (params) => {
      if (isFilterChange.current) {
        isFilterChange.current = false;
        return;
      }
      if (!params) return;
      setScrollSourceRef('dataZoom');
      const { start, end } = params;
      const lastRange = lastRangeRef.current;
      if (start === lastRange.start && end === lastRange.end) return;
      const currentWidth = end - start;

      if (currentWidth > MaxAllowedRangeRef.current.maxAllowedRange) {
        let newEnd;
        if (start === 0) {
          newEnd = MaxAllowedRangeRef.current.maxAllowedRange;
        } else {
          newEnd = start + MaxAllowedRangeRef.current.maxAllowedRange;
        }
        newEnd = Math.min(newEnd, 100);
        instance.current.getEchartsInstance().dispatchAction({
          type: 'dataZoom',
          start: start, // 保持 start 不变
          end: newEnd, // 修正后的 end
          dataZoomIndex: 0, // 对应目标 dataZoom 索引
        });
      } else {
        const chart = instance.current?.getEchartsInstance();
        const { startValue, endValue } = chart.getOption()['dataZoom'][0];
        const sliceTimeline = timelineRef.current.slice(startValue, endValue + 1);
        syncEChartsToGantt(ganttInstance(), sliceTimeline);
      }
      lastRangeRef.current = { start: start, end: end };
    };

    if (!scrollRef.current) {
      // ガント -> リソース山積み
      timelineScrollContainer?.addEventListener(
        'scroll',
        debounce(() => handleHorizontalScrollA(), 100),
      );
      // リソース山積み -> ガント
      instance.current.getEchartsInstance().on(
        'dataZoom',
        debounce((params) => {
          handleHorizontalScrollB(params);
        }, 200),
      );
      instance.current.getEchartsInstance().on('legendselectchanged', (params) => {
        legendselectchange(instance.current.getEchartsInstance(), params);
        const accType = accumulationRef.current?.split('Name')?.[0];
        if (['area', 'discipline'].includes(accType)) {
          ganttInstance().taskStore.clearFilters();
          const selectedKeys = Object.entries(params.selected)
            .filter(([k, v]) => v)
            .map(([k, v]) => k);
          ganttInstance().taskStore.filter({
            filters: [
              {
                id: accType,
                property: accType,
                operator: 'isIncludedIn',
                value: selectedKeys,
              },
            ],
            replace: true,
          });
        }
      });
      scrollRef.current = true;
    }
  };

  useEffect(() => {
    if (!gantt.current || !ganttKey) {
      return;
    }
    const setCheck = (tasks, checked = false) => {
      tasks.forEach((task) => {
        if (task.set) {
          task.set('check', checked);
        } else {
          task.check = checked;
        }
        if (task.children) {
          setCheck(task.children, checked);
        }
      });
    };

    const toggleEventHandler = ({ source, record, checked }) => {
      if (record.children) {
        setCheck(record.children, checked);
      }
      const task = updateTasksCheckStatus(data.tasksData, ganttStore().allRecords);
      setData({
        ...data,
        tasksData: task,
      });
    };

    ganttColumns().get('check').on('toggle', toggleEventHandler);
    ganttInstance().on('taskClick', ({ source, taskRecord, event }) => {
      taskClickHandler({ source, taskRecord, event });
    });
    projectReadyRef.current = false;
    setProjectReady(false);
    ganttProject().on({
      dataReady({ records }) {
        projectReadyRef.current = true;
        setProjectReady(true);
        const testRange = { min: dayjs(), max: dayjs() } as any;
        ganttStore().allRecords.forEach((record) => {
          const { cstrDate, startDate, endDate } = record as any;
          const s = dayjs(startDate);
          const e = dayjs(endDate);
          const c = dayjs(cstrDate);
          if (s.isBefore(testRange.min)) {
            testRange.min = s;
          }
          if (c.isBefore(testRange.min)) {
            testRange.min = c;
          }
          if (e.isAfter(testRange.max)) {
            testRange.max = e;
          }
        });
        minMaxRangeRef.current = testRange;
        testRange.startDate = new Date(testRange.min);
        testRange.endDate = new Date(testRange.max);
        const visibleDateRange = ganttInstance().visibleDateRange as any;
        if (visibleDateRange.startMS < visibleDateRange.endMS) {
          const range = getMaxAllowedRange(ganttProject(), testRange);
          if (range) {
            MaxAllowedRangeRef.current = range;
          }
        }
      },
    });

    ganttProject()
      .loadInlineData(data)
      .then(() => {
        setTimeout(() => {
          if (gantt && gantt.current && ganttInstance) {
            const finalData = [];
            getDataFromTree(finalData, data.tasksData);
            const disciplineColumn: any = ganttColumns().get('discipline');
            disciplineColumn.filterable.filterField.items = uniq(
              finalData.filter((item) => item.discipline).map((item) => item.discipline),
            );
            const areaColumn: any = ganttColumns().get('area');
            areaColumn.filterable.filterField.items = uniq(finalData.filter((item) => item.area).map((item) => item.area));
            const packageColumn: any = ganttColumns().get('package');
            packageColumn.filterable.filterField.items = uniq(finalData.filter((item) => item.package).map((item) => item.package));
          }
          initScrollContainer();
        }, 1000);

        const lockedSubGrid = (ganttInstance().subGrids as any).locked;
        if (lockedSubGrid) {
          lockedSubGrid.width = leftWidth;
        }
      });

    ganttStore().on('filter', ({ filters, source }) => {
      const groupedFilters = filters.allValues.map(({ id, value }) => ({ id, value }));
      if (instance.current) {
        const echart = instance.current.getEchartsInstance();
        ganttFilterRef.current = groupedFilters;
        const { legend } = echart.getOption();
        const acc = accumulationRef.current?.split('Name')?.[0];
        if (groupedFilters?.length && !!acc) {
          const f = groupedFilters.find((f) => f.id === acc);
          if (f) {
            const selected = Object.fromEntries(
              legend[0].data.map((l) => [l.name, !f.value.length || f.value.includes(l.name) || l.name === 'total']),
            );
            echart.setOption({
              legend: [{ ...legend[0], selected }],
            });
          }
        }
      }
    });

    ganttInstance().on('beforePdfExport', ({ config }) => {
      exportHtmlRef.current.config = config;
    });
  }, [ganttKey]);

  const getDataFromTree = (finalData: any[], treeData: any[]) => {
    treeData.forEach((treeNode) => {
      if (treeNode.children && treeNode.children.length > 0) {
        getDataFromTree(finalData, treeNode.children);
      } else {
        finalData.push(treeNode);
      }
    });
  };

  const toTaskPosition = (taskId) => {
    setAlertView({
      key: taskId,
      tab: 'S&R',
    });
  };

  const [predecessorColumns] = useState([
    {
      key: 'Predecessor',
      title: 'Predecessor',
      children: [
        {
          key: 'PredecessorTaskId',
          dataIndex: 'taskId',
          title: t('aipskd.optionDetail.taskId'),
          ellipsis: true,
          width: 130,
          render: (text, record, index) => (
            <Button
              size="small"
              style={{ padding: 0 }}
              type="link"
              className="link-ellipsis"
              onClick={() => {
                toTaskPosition(text);
              }}
            >
              {text}
            </Button>
          ),
        },
        {
          key: 'PredecessorTaskName',
          dataIndex: 'taskName',
          title: t('aipskd.optionDetail.taskName'),
          ellipsis: true,
          width: 180,
        },
        {
          key: 'PredecessorStartDate',
          dataIndex: 'startDate',
          title: t('aipskd.optionDetail.taskStartDate'),
          ellipsis: true,
          width: 100,
        },
        {
          key: 'PredecessorEndDate',
          dataIndex: 'endDate',
          title: t('aipskd.optionDetail.taskEndDate'),
          ellipsis: true,
          width: 100,
        },
        {
          key: 'PredecessorDependencyType',
          dataIndex: 'dependencyType',
          title: 'Ralation',
          ellipsis: true,
          width: 70,
        },
        {
          key: 'PredecessorLag',
          dataIndex: 'lag',
          title: 'Lag',
          ellipsis: true,
          width: 60,
        },
        {
          key: 'PredecessorFreeFloat',
          dataIndex: 'freeFloat',
          title: 'Free Float',
          ellipsis: true,
        },
        {
          key: 'PredecessorDriving',
          dataIndex: 'driving',
          title: 'Driving',
          ellipsis: true,
          filterMultiple: false,
          filters: [
            {
              text: 1,
              value: 1,
            },
            {
              text: 0,
              value: 0,
            },
          ],
          onFilter: (value, record) => record.driving === value,
        },
      ],
    },
  ]);
  const [successorColumns] = useState([
    {
      key: 'Successor',
      title: 'Successor',
      children: [
        {
          key: 'SuccessorTaskId',
          dataIndex: 'taskId',
          title: t('aipskd.optionDetail.taskId'),
          ellipsis: true,
          width: 130,
          render: (text, record, index) => (
            <Button
              size="small"
              style={{ padding: 0 }}
              type="link"
              className="link-ellipsis"
              onClick={() => {
                toTaskPosition(text);
              }}
            >
              {text}
            </Button>
          ),
        },
        {
          key: 'SuccessorTaskName',
          dataIndex: 'taskName',
          title: t('aipskd.optionDetail.taskName'),
          ellipsis: true,
          width: 180,
        },
        {
          key: 'SuccessorStartDate',
          dataIndex: 'startDate',
          title: t('aipskd.optionDetail.taskStartDate'),
          ellipsis: true,
          width: 100,
        },
        {
          key: 'SuccessorEndDate',
          dataIndex: 'endDate',
          title: t('aipskd.optionDetail.taskEndDate'),
          ellipsis: true,
          width: 100,
        },
        {
          key: 'SuccessorDependencyType',
          dataIndex: 'dependencyType',
          title: 'Ralation',
          ellipsis: true,
          width: 70,
        },
        {
          key: 'SuccessorLag',
          dataIndex: 'lag',
          title: 'Lag',
          ellipsis: true,
          width: 60,
        },
        {
          key: 'SuccessorFreeFloat',
          dataIndex: 'freeFloat',
          title: 'Free Float',
          ellipsis: true,
        },
        {
          key: 'SuccessorDriving',
          dataIndex: 'driving',
          title: 'Driving',
          ellipsis: true,
          filterMultiple: false,
          filters: [
            {
              text: 1,
              value: 1,
            },
            {
              text: 0,
              value: 0,
            },
          ],
          onFilter: (value, record) => record.driving === value,
        },
      ],
    },
  ]);

  const [activeTask, setActiveTask] = useState();
  const [predecessorData, setPredecessorData] = useState([]);
  const [successorData, setSuccessorData] = useState([]);

  const taskClickHandler = ({ source, taskRecord, event }) => {
    if (!projectReadyRef.current) {
      return;
    }
    if (taskRecord.children) {
      return;
    }

    modeDispatch({ type: 'showTaskCard' });
    const typeList: any = {
      0: 'SS',
      1: 'SF',
      2: 'FS',
      3: 'FF',
    };
    const { taskId, dependencies } = taskRecord;
    setActiveTask(taskId);
    const dataInfo = dependencies.reduce(
      (acc, cur) => {
        if (cur.to === taskId) {
          const task = cur.fromTask;
          acc.predecessor.push({
            taskId: task.taskId,
            taskName: task.name,
            startDate: getDayString(task.startDate),
            endDate: getDayString(task.endDate),
            dependencyType: typeList[cur.type],
            lag: cur.lag,
            freeFloat: cur.freeFloat,
            driving: cur.isDrivingPath ? 1 : 0,
          });
        }
        if (cur.from === taskId) {
          const task = cur.toTask;
          acc.successor.push({
            taskId: task.taskId,
            taskName: task.name,
            startDate: getDayString(task.startDate),
            endDate: getDayString(task.endDate),
            dependencyType: typeList[cur.type],
            lag: cur.lag,
            freeFloat: cur.freeFloat,
            driving: cur.isDrivingPath ? 1 : 0,
          });
        }
        return acc;
      },
      {
        predecessor: [],
        successor: [],
      },
    );

    setPredecessorData(dataInfo.predecessor);
    setSuccessorData(dataInfo.successor);
  };

  useEffect(() => {
    if (calenderOrg) {
      const calenderData = calenderOrg.filter((calender) => calender.workingDay);
      setCalenderData(calenderData);
    }
  }, [calenderOrg]);

  const [mpMaxValue, setMpMaxValue] = useState(0);
  const [defaultMaxValue, setDefaultMaxValue] = useState(0);
  useEffect(() => {
    if (!mpMaxValue) {
      const yAxis = { ...option.yAxis };
      delete yAxis.max;
      setOption({ ...option, yAxis: { ...yAxis } });
      return;
    }
    setOption({ ...option, yAxis: { ...option.yAxis, max: mpMaxValue } });
  }, [mpMaxValue]);
  const handleMpMaxChange = (value: number) => {
    setMpMaxValue(value);
  };
  const handleResetMpMaxValue = () => {
    setMpMaxValue(defaultMaxValue);
  };

  useImperativeHandle(
    ref,
    () => ({
      ganttInstance: () => ganttInstance(),
      projectReadyRef: () => projectReadyRef.current,
    }),
    [projectReadyRef.current],
  );

  return (
    <>
      <div className="bryntum-gantt-container">
        <BryntumGantt key={ganttKey} ref={gantt} height={height} {...ganttCardConfig} />
        <Card
          style={{
            display: mode.viewMode === 'table-only' && !collapsed ? 'block' : 'none',
          }}
          styles={{
            body: {
              height: 320,
              paddingLeft: 380,
              position: 'relative',
              width: '102%',
            },
          }}
        >
          {scrollSourceRef === 'dataZoom' && (
            <div
              style={{
                position: 'absolute',
                top: 20,
                right: 46,
                paddingLeft: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                zIndex: 99,
              }}
            >
              <Button
                size="small"
                onClick={() => {
                  ganttInstance().zoomTo({
                    preset: 'monthAndYear',
                  });
                  setScrollSourceRef('user');
                }}
              >
                Cancel Chart Zoom
              </Button>
            </div>
          )}
          <div
            style={{
              position: 'absolute',
              left: -30,
              top: 35,
              width: 180,
              paddingLeft: 0,
            }}
          >
            {widget}
          </div>

          <div
            style={{
              position: 'absolute',
              left: -30,
              top: 100,
              width: leftWidth,
              paddingLeft: 40,
            }}
          >
            <Radio.Group
              options={filterOption.filterOptions}
              onChange={onTimeTypeChange}
              value={filterType}
              optionType="button"
              buttonStyle="solid"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            />
          </div>
          <Space
            style={{
              position: 'absolute',
              left: -30,
              bottom: 26,
              width: leftWidth,
              paddingLeft: 40,
            }}
          >
            <InputNumber value={mpMaxValue} onChange={handleMpMaxChange} controls={false} style={{ width: 316 }} />
            <Button onClick={handleResetMpMaxValue}>Default</Button>
          </Space>
          <MeteorEchart chartRef={instance} option={{ ...option, echartTheme }} style={{ height: '320px' }} opts={{ renderer: 'canvas' }} />
        </Card>

        {mode.viewMode === 'table-card' && (
          <Card
            styles={{
              header: {
                minHeight: 42,
              },
              body: {
                height: 255,
              },
            }}
            title={
              <span
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  toTaskPosition(activeTask);
                }}
              >
                <Button style={{ color: '#1a4f99', fontWeight: '500', padding: 0 }} type="link" size="large">
                  {activeTask}
                </Button>
              </span>
            }
            extra={
              <CloseOutlined
                onClick={() => {
                  modeDispatch({ type: 'table-mp' });
                }}
              />
            }
          >
            <Row>
              <Col span={12}>
                <Table
                  bordered
                  scroll={{
                    y: 152,
                  }}
                  size="small"
                  pagination={false}
                  columns={predecessorColumns}
                  dataSource={predecessorData}
                />
              </Col>
              <Col span={12}>
                <Table
                  bordered
                  scroll={{
                    y: 152,
                  }}
                  size="small"
                  pagination={false}
                  columns={successorColumns}
                  dataSource={successorData}
                />
              </Col>
            </Row>
          </Card>
        )}
      </div>
    </>
  );
});
export default GanttChartWithMp;
